import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import Button from '../../ui/Button';
import '../../../styles/SupportDashboard.css';

export default function ManageGroupModal({ isOpen, onClose, sites = [], onRename, onDelete }) {
  const [groupId, setGroupId] = useState('');
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const selected = sites.find(site => String(site.id) === groupId);

  useEffect(() => {
    if (!isOpen) return;
    setGroupId(''); setName(''); setQuery(''); setConfirmDelete(false);
    setBusy(''); setError(''); setSuccess('');
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  const selectGroup = (id) => {
    setGroupId(id);
    setName(sites.find(site => String(site.id) === id)?.sitename || '');
    setConfirmDelete(false); setError(''); setSuccess('');
  };

  const submit = async (action) => {
    if (!selected || busy || (action === 'delete' && !confirmDelete)) return;
    const trimmedName = name.trim();
    if (action === 'rename' && (!trimmedName || trimmedName.length > 200)) {
      setError('Enter a group name between 1 and 200 characters.');
      return;
    }
    setBusy(action); setError(''); setSuccess('');
    try {
      if (action === 'rename') {
        await onRename(selected.id, trimmedName);
        setName(trimmedName);
        setSuccess(`Group renamed to “${trimmedName}”.`);
      } else {
        await onDelete(selected.id);
        setGroupId(''); setName(''); setConfirmDelete(false);
        setSuccess(`Group “${selected.sitename}” deleted.`);
      }
    } catch (err) {
      const body = err?.body || err?.response?.data;
      const details = Array.isArray(body?.errors)
        ? body.errors.map(item => item.message).filter(Boolean).join(' ')
        : Object.values(body?.errors || {}).flat().join(' ');
      setError(details || body?.message || err.message || 'Unable to update the group.');
    } finally {
      setBusy('');
    }
  };

  const options = sites.filter(site => String(site.id) === groupId ||
    `${site.sitename} ${site.level || ''} ${site.id}`.toLowerCase().includes(query.toLowerCase().trim()));

  return (
    <Modal isOpen={isOpen} onRequestClose={() => !busy && onClose()}
      appElement={document.getElementById('root') || undefined}
      className="sd-modal sd-modal-md" overlayClassName="sd-modal-backdrop"
      aria={{ labelledby: 'manage-group-title' }} shouldCloseOnOverlayClick={false}>
      <div className="sd-modal-header">
        <div><h2 id="manage-group-title" className="sd-title">Manage group</h2>
          <div className="sd-subtitle">Edit a group name or delete an empty group.</div></div>
        <button type="button" className="sd-icon-btn" aria-label="Close" disabled={!!busy} onClick={onClose}>✕</button>
      </div>
      <div className="sd-modal-body">
        {error && <div className="sd-alert sd-alert-bad" role="alert">{error}</div>}
        {success && <div className="sd-alert sd-alert-good" role="status">{success}</div>}
        <div className="sd-field">
          <label className="sd-label" htmlFor="group-search">Search groups</label>
          <input id="group-search" className="sd-input" value={query} disabled={!!busy}
            onChange={event => setQuery(event.target.value)} placeholder="Search by name or group ID" />
        </div>
        <div className="sd-field">
          <label className="sd-label" htmlFor="managed-group">Group</label>
          <select id="managed-group" className="sd-input" value={groupId} disabled={!!busy}
            onChange={event => selectGroup(event.target.value)}>
            <option value="">Select a group…</option>
            {options.map(site => <option key={site.id} value={String(site.id)}>
              {site.sitename} {site.level ? `(${site.level})` : ''} — #{site.id}
            </option>)}
          </select>
          {!options.length && <div className="sd-help">No groups found.</div>}
        </div>
        {selected && <>
          <form onSubmit={event => { event.preventDefault(); submit('rename'); }}>
            <div className="sd-field">
              <label className="sd-label" htmlFor="group-name">Group name</label>
              <input id="group-name" className="sd-input" value={name} maxLength={200} required
                disabled={!!busy} onChange={event => setName(event.target.value)} />
            </div>
            <Button type="submit" disabled={!!busy || !name.trim() || name.trim() === selected.sitename}>
              {busy === 'rename' ? 'Saving…' : 'Save name'}
            </Button>
          </form>
          <div className="sd-danger-zone">
            <h5 className="sd-danger-title">Delete group</h5>
            <p id="delete-group-warning">Before deleting this group, please move all devices out of it and into the correct group. This action cannot be undone.</p>
            <label className="sd-help">
              <input type="checkbox" checked={confirmDelete} disabled={!!busy}
                onChange={event => setConfirmDelete(event.target.checked)} />{' '}
              I have moved all devices and want to delete “{selected.sitename}”.
            </label>
            <button type="button" className="sd-danger-btn" aria-describedby="delete-group-warning"
              disabled={!!busy || !confirmDelete} onClick={() => submit('delete')}>
              {busy === 'delete' ? 'Deleting…' : 'Delete group'}
            </button>
          </div>
        </>}
      </div>
      <div className="sd-modal-footer">
        <Button type="button" buttonStyle="btn-alternate" disabled={!!busy} onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}
